import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { EditOutlined } from "@material-ui/icons";
import React from "react";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import { safeDateFormat } from "../../utils";

const useStyles = makeStyles((theme) => ({
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "12px 10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "12px 10px",
    fontSize: "0.875rem",
  },
  errorText: {
    color: theme.palette.error.main,
    padding: "1rem",
  },
  prayerCount: {
    borderBottom: "1px dashed #bdbdbd",
    cursor: "help",
  },
}));

const formatCurrency = (val) => {
  if (val === undefined || val === null || isNaN(parseFloat(val))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(val);
};

// The list payload has no dedicated "challan generated on" field yet, so fall
// back through the dated fields that travel with a challan number.
const challanGeneratedOn = (item) =>
  item.challan_generated_at ||
  item.challan_date ||
  item.receipt_date ||
  item.created_at;

function PrayerCount({ item, classes }) {
  const count =
    item.number_of_prayers !== undefined && item.number_of_prayers !== null
      ? item.number_of_prayers
      : (item.prayers || []).length;

  if (!item.prayers || item.prayers.length === 0) {
    return <span>{count || "—"}</span>;
  }

  return (
    <Tooltip
      title={
        <Box>
          {item.prayers.map((p, i) => (
            <Typography
              key={p.prayer_number || i}
              variant="caption"
              style={{ display: "block" }}
            >
              {p.prayer_number || i + 1}. {p.description}
            </Typography>
          ))}
        </Box>
      }
    >
      <span className={classes.prayerCount}>{count}</span>
    </Tooltip>
  );
}

export default function FeesCalculationTab({
  rows,
  loading,
  error,
  page,
  rowsPerPage,
  totalCount,
  onChangePage,
  onEdit,
}) {
  const classes = useStyles();

  return (
    <>
      {error && (
        <Typography className={classes.errorText} variant="body2">
          {error}
        </Typography>
      )}

      {loading ? (
        <Box p={4}>
          <CircularCenteredLoader />
        </Box>
      ) : (
        <Paper
          style={{
            width: "100%",
            overflowX: "auto",
            boxShadow: "none",
            border: "1px solid #ddd",
          }}
        >
          <TableContainer>
            <Table size="small" style={{ maxWidth: "100%", marginTop: 0 }}>
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Case ID</TableCell>
                  <TableCell>Sum in Dispute</TableCell>
                  <TableCell>Total Fee</TableCell>
                  <TableCell>Fee Paid</TableCell>
                  <TableCell>No. of Prayers</TableCell>
                  <TableCell>Challan No.</TableCell>
                  <TableCell>Challan Status</TableCell>
                  <TableCell>Challan Generated On</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      style={{ padding: "2rem" }}
                    >
                      <Typography color="textSecondary">
                        No fee calculations found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((item, idx) => {
                    const serialNo = totalCount - (page * rowsPerPage + idx);
                    const hasChallan = Boolean(item.challan_number);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell className={classes.tableBodyCell}>
                          {serialNo}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {item.case_number || item.dispute_id || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(item.claim_amount)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(
                            item.total_fee !== undefined && item.total_fee !== null
                              ? item.total_fee
                              : item.total_amount
                          )}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {formatCurrency(item.amount_paid)}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <PrayerCount item={item} classes={classes} />
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {item.challan_number || "—"}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          <Chip
                            size="small"
                            label={hasChallan ? "Generated" : "Not Generated"}
                            style={{
                              backgroundColor: hasChallan ? "#E4F5EA" : "#F2F4F7",
                              color: hasChallan ? "#27AE60" : "#475467",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {hasChallan
                            ? safeDateFormat(challanGeneratedOn(item))
                            : "—"}
                        </TableCell>
                        <TableCell
                          className={classes.tableBodyCell}
                          align="center"
                        >
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(item)}>
                              <EditOutlined fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={onChangePage}
          />
        </Paper>
      )}
    </>
  );
}
